import {
  personHistoryEntrySchema,
  personHistoryResponseSchema,
  type PersonHistoryEntry,
  type PersonHistoryInput,
  type PersonHistoryResponse,
  healthResponseSchema,
  controlledFormChoicesSchema,
  issueControlledFormSchema,
  stepInstanceSchema,
  type IssueControlledForm,
  agencyInterviewersSchema,
  interviewerListCommandSchema,
  catalogOptionsSchema,
  onboardingPreviewSchema,
  personChecklistSchema,
  personSchema,
  personWithProfileAnswersSchema,
  type AgencyInterviewers,
  type InterviewerListCommand,
  type CatalogOptions,
  type CreatePerson,
  type CreateStaffMember,
  type ChangeLifecycle,
  type DirectRecordInput,
  type HealthResponse,
  type LifecycleState,
  type Person,
  type PersonWithProfileAnswers,
  type UpdatePersonReason,
  type PersonChecklist,
  type OnboardingPreview,
  type RejectionReason,
  type UpdatePerson,
  apiErrorSchema,
  preferencesViewSchema,
  placeAutocompleteRequestSchema,
  placeAutocompleteResponseSchema,
  placeNamesResponseSchema,
  coverageMapResponseSchema,
  type CoverageMapResponse,
  placeResolveRequestSchema,
  placeResolveResponseSchema,
  type PlaceAutocompleteRequest,
  type PlaceAutocompleteResponse,
  type PlaceNamesResponse,
  type PlaceResolveRequest,
  type PlaceResolveResponse,
  createSavedFilterResponseSchema,
  createSavedFilterRequestSchema,
  renameSavedFilterRequestSchema,
  setLastFiltersRequestSchema,
  type PreferencesView,
  type RosterFilterPreference,
  type SavedRosterFilter,
  stepAnswersSchema,
  personStepsSchema,
  type StepAnswers,
  type PersonStepSummary,
  sensitiveOnFileSchema,
  sensitiveRevealRequestSchema,
  sensitiveRevealResponseSchema,
  sensitiveUpdateSchema,
  type SensitiveOnFile,
  type SensitiveRevealRequest,
  type SensitiveRevealResponse,
  type SensitiveUpdate,
  dashboardSchema,
  importResultSchema,
  issuedPortalLinkSchema,
  issuePortalLinkRequestSchema,
  deliveredPortalLinkSchema,
  portalLinkStatusSchema,
  portalLinkRevokedSchema,
  type DeliveredPortalLink,
  type IssuePortalLinkRequest,
  type PortalLinkRevoked,
  type PortalLinkStatus,
  interviewsSchema,
  offersSchema,
  peopleListSchema,
  portalLinksSchema,
  reviewQueueSchema,
  type Dashboard,
  type Interviews,
  type Offers,
  type ImportResult,
  type PortalLinks,
  type IssuedPortalLink,
  type ReviewQueue,
} from '@vigeo/shared';

/**
 * Responses are parsed with the same schema the API validated them against
 * (CLAUDE.md rule 3) — the shape is defined exactly once, in packages/shared.
 */
export async function fetchHealth(): Promise<HealthResponse> {
  const response = await fetch('/api/health');
  if (!response.ok) {
    throw new Error(`Health check failed with status ${response.status}`);
  }
  return healthResponseSchema.parse(await response.json());
}

/**
 * Every response is parsed with the schema packages/shared owns and the API validated
 * against (CLAUDE.md rule 3). The client never re-derives status, progress or visibility —
 * it renders what the one derivation returned.
 */

async function getJson(path: string): Promise<unknown> {
  const response = await fetch(path);
  if (!response.ok) {
    // The status rides on the error so the app can tell "no such thing" (404, not retried) from a fault (query-retry.ts).
    throw Object.assign(new Error(`Request to ${path} failed with status ${response.status}`), { status: response.status });
  }
  return response.json();
}

export async function fetchPeople(lifecycle?: LifecycleState): Promise<readonly Person[]> {
  const query = lifecycle === undefined ? '' : `?lifecycle=${encodeURIComponent(lifecycle)}`;
  const body = await getJson(`/api/people${query}`);
  return peopleListSchema.parse(body).people;
}

export async function fetchPerson(id: string): Promise<PersonWithProfileAnswers> {
  return personWithProfileAnswersSchema.parse(await getJson(`/api/people/${encodeURIComponent(id)}`));
}

export async function fetchPersonHistory(id: string): Promise<PersonHistoryResponse> {
  return personHistoryResponseSchema.parse(await getJson(`/api/people/${encodeURIComponent(id)}/history`));
}

export async function appendPersonHistory(id: string, input: PersonHistoryInput): Promise<PersonHistoryEntry> {
  return personHistoryEntrySchema.parse(await postJson(`/api/people/${encodeURIComponent(id)}/history`, input));
}

export async function fetchChecklist(id: string): Promise<PersonChecklist> {
  return personChecklistSchema.parse(await getJson(`/api/people/${encodeURIComponent(id)}/checklist`));
}

/**
 * What moving this person to Onboarding would add to their portal (#264).
 *
 * Read-only and derived from `planPacket`, the same function the move itself creates from, so the
 * preview cannot promise a packet the move will not produce.
 */
export async function fetchOnboardingPreview(id: string): Promise<OnboardingPreview> {
  return onboardingPreviewSchema.parse(await getJson(`/api/people/${encodeURIComponent(id)}/onboarding-preview`));
}

/**
 * Role and tag options come from the seeded catalog via the API — never a list typed into
 * a component. A renamed profile moves every picker with no code change.
 */
/** The dashboard's figures, each from its source query at the moment of the request. */
export async function fetchDashboard(): Promise<Dashboard> {
  return dashboardSchema.parse(await getJson('/api/dashboard'));
}

/** Every candidate's offer, grouped by where it stands (#259). Derived per request, never stored. */
export async function fetchOffers(): Promise<Offers> {
  return offersSchema.parse(await getJson('/api/offers'));
}

/** Every candidate's interview date and interviewers, in one bounded read (#303, #802). */
export async function fetchInterviews(): Promise<Interviews> {
  return interviewsSchema.parse(await getJson('/api/interviews'));
}

/**
 * Every live portal link and where it stands, in one bounded read (#303, #802). Carries no token,
 * no digest and no access code: `portalLinkRowSchema` is `.strict()`, so one arriving would fail the parse.
 */
export async function fetchPortalLinks(): Promise<PortalLinks> {
  return portalLinksSchema.parse(await getJson('/api/portal-links'));
}

export async function fetchCatalogOptions(): Promise<CatalogOptions> {
  return catalogOptionsSchema.parse(await getJson('/api/catalog/options'));
}

/**
 * Who may be picked as an interviewer (#341). An AGENCY CHOICE, published in HR settings, so Paul
 * can add a person without a deploy — the reason this is fetched rather than listed in the form.
 */
/**
 * ADR-012 §4 (#203). The masked read: two booleans and the last four, never a value.
 *
 * Safe to cache like any other person read — it holds nothing that a reveal would. The REVEAL is
 * the one that must never reach a cache, and it is a POST for that reason among others.
 */
export async function fetchSensitiveOnFile(id: string): Promise<SensitiveOnFile> {
  return sensitiveOnFileSchema.parse(await getJson(`/api/people/${encodeURIComponent(id)}/sensitive`));
}

/**
 * The one call that returns a Social Security number or a date of birth.
 *
 * Its result is deliberately NOT a query and NOT cached: the caller holds it in component state,
 * re-masks on a timer and on unmount, and it never reaches the TanStack cache, storage or a URL
 * (ADR-012 §4). A `useQuery` here would put it in a cache that outlives the screen.
 */
export async function revealSensitive(id: string, request: SensitiveRevealRequest): Promise<SensitiveRevealResponse> {
  const body = await postJson(`/api/people/${encodeURIComponent(id)}/sensitive/reveal`, sensitiveRevealRequestSchema.parse(request));
  return sensitiveRevealResponseSchema.parse(body);
}

/** HR enters or corrects a value. 204, so there is nothing to parse and nothing to show. */
export async function updateSensitive(id: string, input: SensitiveUpdate): Promise<void> {
  await putJson(`/api/people/${encodeURIComponent(id)}/sensitive`, sensitiveUpdateSchema.parse(input));
}

/**
 * What the candidate has entered on ONE form, for HR's two tabs (ADR-013 §4, #211).
 *
 * A 404 here carries no information on purpose: the endpoint answers the same way for a form
 * that does not exist, one belonging to somebody else, and one this user may not read — a
 * segregated health form needs `answers.restricted.read`, and saying "forbidden" would confirm
 * the form exists. The caller must therefore render "not available" rather than "no permission",
 * because it genuinely cannot tell the two apart.
 */
/**
 * The person record's form list. A caller without `answers.restricted.read` simply gets fewer rows
 * — a segregated form is omitted rather than shown as a locked placeholder, because a placeholder
 * still says one exists (ADR-013 §4).
 */
export async function fetchPersonSteps(personId: string): Promise<readonly PersonStepSummary[]> {
  const body = await getJson(`/api/people/${encodeURIComponent(personId)}/steps`);
  return personStepsSchema.parse(body).steps;
}

export async function fetchStepAnswers(personId: string, instanceId: string): Promise<StepAnswers> {
  const body = await getJson(
    `/api/people/${encodeURIComponent(personId)}/steps/${encodeURIComponent(instanceId)}/answers`,
  );
  return stepAnswersSchema.parse(body);
}

export async function fetchAgencyInterviewers(): Promise<AgencyInterviewers> {
  return agencyInterviewersSchema.parse(await getJson('/api/agency/interviewers'));
}

/**
 * Add a name to the agency's interviewer list, or take one off it (#372 part 4).
 *
 * Returns the list AS THE SERVER NOW HOLDS IT, so the caller replaces what it is showing rather
 * than patching its own copy — two HR users editing at once then converge instead of each keeping
 * their own idea of the list.
 */
export async function changeAgencyInterviewers(command: InterviewerListCommand): Promise<AgencyInterviewers> {
  return agencyInterviewersSchema.parse(await postJson('/api/agency/interviewers', interviewerListCommandSchema.parse(command)));
}

export interface ApiError {
  readonly message: string;
  readonly issues: readonly string[];
  /**
   * The HTTP status, so a caller can tell a refusal apart from an outage without matching on the
   * message text. #203 needs it: a 429 means wait an hour, a 503 means try again now, and the two
   * want different words — matching prose to decide that is the kind of coupling that breaks the
   * day somebody improves the wording.
   */
  readonly status: number;
}

/** Same error handling as `postJson`; a 204 has no body to read. */
async function putJson(path: string, payload: unknown): Promise<void> {
  const response = await fetch(path, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (response.ok) return;
  const parsed = apiErrorSchema.safeParse(await response.json().catch(() => ({})));
  const error: ApiError = parsed.success
    ? { message: parsed.data.error, issues: parsed.data.issues, status: response.status }
    : { message: `Request failed with status ${response.status}`, issues: [], status: response.status };
  throw Object.assign(new Error(error.message), error);
}

async function postJson(path: string, payload: unknown, headers: Record<string, string> = {}): Promise<unknown> {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...headers },
    body: JSON.stringify(payload),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(body);
    const error: ApiError = parsed.success
      ? { message: parsed.data.error, issues: parsed.data.issues, status: response.status }
      : { message: `Request failed with status ${response.status}`, issues: [], status: response.status };
    throw Object.assign(new Error(error.message), error);
  }
  return body;
}

/**
 * `idempotencyKey` is one value per form submission, kept across retries: the API derives
 * the person's id from it, so a retried request meets the person it created.
 */
export async function createPerson(input: CreatePerson, options: { readonly idempotencyKey?: string } = {}): Promise<Person> {
  const headers: Record<string, string> = {};
  if (options.idempotencyKey !== undefined) headers['idempotency-key'] = options.idempotencyKey;
  return personSchema.parse(await postJson('/api/people', input, headers));
}

/**
 * Add an existing staff member directly as Active (ADR-016, #459). Its own route, apart from
 * `createPerson`: that one makes a candidate. One idempotency key per form opening, as there.
 */
export async function createStaffMember(input: CreateStaffMember, options: { readonly idempotencyKey?: string } = {}): Promise<Person> {
  const headers: Record<string, string> = {};
  if (options.idempotencyKey !== undefined) headers['idempotency-key'] = options.idempotencyKey;
  return personSchema.parse(await postJson('/api/staff', input, headers));
}

/**
 * A lifecycle move is its own request with its own reason — a PATCH that silently
 * accepted `lifecycle` would be a conversion nobody reviewed, so the API refuses it there.
 */
export async function changeLifecycle(
  id: string,
  input: ChangeLifecycle,
): Promise<Person> {
  return personSchema.parse(await postJson(`/api/people/${encodeURIComponent(id)}/lifecycle`, input));
}

async function patchJson(path: string, payload: unknown): Promise<unknown> {
  const response = await fetch(path, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(body);
    const error: ApiError = parsed.success
      ? { message: parsed.data.error, issues: parsed.data.issues, status: response.status }
      : { message: `Request failed with status ${response.status}`, issues: [], status: response.status };
    throw Object.assign(new Error(error.message), error);
  }
  return body;
}

/** `reason` rides beside the patch and goes to the audit row, never into the record (ADR-013 §2). */
export async function updatePerson(id: string, input: UpdatePerson, options: { readonly reason?: string } = {}): Promise<Person> {
  const request: UpdatePerson & UpdatePersonReason = options.reason === undefined ? input : { ...input, reason: options.reason };
  return personSchema.parse(await patchJson(`/api/people/${encodeURIComponent(id)}`, request));
}

export type PortalLink = IssuedPortalLink;

export async function issuePortalLink(id: string): Promise<PortalLink> {
  // The request shape is the API's own schema, so a reveal body cannot drift from what it accepts.
  const request: IssuePortalLinkRequest = { delivery: 'reveal' };
  const body = await postJson(`/api/people/${encodeURIComponent(id)}/portal-link`, issuePortalLinkRequestSchema.parse(request));
  return issuedPortalLinkSchema.parse(body);
}

/**
 * Where this person's portal link stands (#471). Carries NO secret — not the token, not the
 * access code — which the schema enforces with `.strict()` and the API's own tests assert
 * against the wire body. So this is safe to ask on every render of step 3, which is the point:
 * the frame's Link block is a status display, and a status display must not read a credential.
 */
export async function fetchPortalLinkStatus(id: string): Promise<PortalLinkStatus> {
  return portalLinkStatusSchema.parse(await getJson(`/api/people/${encodeURIComponent(id)}/portal-link`));
}

/**
 * The frame's **Email a new link**: issue a link and SEND it, so no copy of the code reaches
 * this browser. `delivered: false` is an ordinary answer, not a failure — mail is off by
 * configuration in both environments today — and the caller shows the reason and falls back to
 * handing the link over by hand.
 */
export async function emailPortalLink(id: string): Promise<DeliveredPortalLink> {
  const request: IssuePortalLinkRequest = { delivery: 'email' };
  const body = await postJson(`/api/people/${encodeURIComponent(id)}/portal-link`, issuePortalLinkRequestSchema.parse(request));
  return deliveredPortalLinkSchema.parse(body);
}

/** The frame's **Revoke link**. `revoked: false` means there was nothing live to revoke. */
export async function revokePortalLink(id: string): Promise<PortalLinkRevoked> {
  return portalLinkRevokedSchema.parse(await postJson(`/api/people/${encodeURIComponent(id)}/portal-link/revoke`, {}));
}

export type { ImportResult } from '@vigeo/shared';

/**
 * The server validates the whole file again before writing. The client-side preview uses
 * the same `planCsvImport` from packages/shared, so the two cannot disagree.
 */
export async function importPeople(csv: string): Promise<ImportResult> {
  const body = await postJson('/api/people/import', { csv });
  return importResultSchema.parse(body);
}

/* -------------------------------------------------------------------------------------
 * The review queue (#59).
 * ---------------------------------------------------------------------------------- */

export { reviewItemSchema, reviewQueueSchema, type ReviewItem, type ReviewQueue } from '@vigeo/shared';

export async function fetchReviewQueue(cursor?: string | null): Promise<ReviewQueue> {
  const query = cursor === undefined || cursor === null ? '' : `?cursor=${encodeURIComponent(cursor)}`;
  return reviewQueueSchema.parse(await getJson(`/api/review${query}`));
}

/**
 * The person id travels as a query parameter because `instances` is partitioned on it and
 * the API does a point read. It is an opaque uuid, never a name — CLAUDE.md rule 8's "no
 * PII in URLs" is about identifiable values, and this is the same id already in the path of
 * every person page.
 */
function decisionPath(instanceId: string, personId: string, verb: 'approve' | 'reject'): string {
  return `/api/review/${encodeURIComponent(instanceId)}/${verb}?personId=${encodeURIComponent(personId)}`;
}

export async function approveSubmission(instanceId: string, personId: string): Promise<void> {
  await postJson(decisionPath(instanceId, personId, 'approve'), {});
}

export async function rejectSubmission(
  instanceId: string,
  personId: string,
  decision: { readonly reason: RejectionReason; readonly note: string; readonly candidateComment: string },
): Promise<void> {
  await postJson(decisionPath(instanceId, personId, 'reject'), decision);
}

/**
 * HR closing a requirement themselves (#85).
 *
 * Eight blocking requirements had no path to satisfaction at all: orientation, three
 * external checks, and four qualifications HR reviews. Without this a candidate could finish
 * everything the portal asked and stay "Not ready" forever.
 */
export async function recordRequirement(
  personId: string,
  requirementKey: string,
  record: DirectRecordInput,
): Promise<void> {
  await postJson(
    `/api/people/${encodeURIComponent(personId)}/requirements/${encodeURIComponent(requirementKey)}/record`,
    record,
  );
}

export async function fetchControlledForms(id: string) {
  return controlledFormChoicesSchema.parse(await getJson(`/api/people/${encodeURIComponent(id)}/controlled-forms`));
}
export async function issueControlledForm(id: string, input: IssueControlledForm) {
  return stepInstanceSchema.parse(await postJson(`/api/people/${encodeURIComponent(id)}/controlled-forms`, issueControlledFormSchema.parse(input)));
}


/* ---- A user's own remembered and saved filters (ADR-019, #476) ---------------------------------- */

/** A refusal from the preferences routes. `code` is the API's own word for it (`name-taken`, `limit`, `conflict`). */
export type PreferencesError = ApiError & { readonly code: string | null };

async function preferencesRequest(method: 'GET' | 'PUT' | 'POST' | 'PATCH' | 'DELETE', path: string, payload?: unknown, options: { readonly keepalive?: boolean } = {}): Promise<unknown> {
  const response = await fetch(path, {
    method,
    // The remembered-filters write may go out as the page is hidden or left; keepalive lets it finish.
    keepalive: options.keepalive === true,
    headers: payload === undefined ? {} : { 'content-type': 'application/json' },
    body: payload === undefined ? undefined : JSON.stringify(payload),
  });
  const body: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(body);
    const code = typeof (body as { code?: unknown } | null)?.code === 'string' ? (body as { code: string }).code : null;
    const error: PreferencesError = parsed.success
      ? { message: parsed.data.error, issues: parsed.data.issues, status: response.status, code }
      : { message: `Request failed with status ${response.status}`, issues: [], status: response.status, code };
    throw Object.assign(new Error(error.message), error);
  }
  return body;
}

const PREFERENCES_ROSTER = '/api/preferences/roster';

/** Only ever called for an Admin: a Viewer's roster makes no preference request at all (ADR-019 §3a). */
export async function fetchPreferences(): Promise<PreferencesView> {
  return preferencesViewSchema.parse(await preferencesRequest('GET', '/api/preferences'));
}

/** The filters the roster is showing now, so they come back next time. The search text is not part of the type. */
export async function setLastFilters(filters: RosterFilterPreference): Promise<PreferencesView> {
  return preferencesViewSchema.parse(await preferencesRequest('PUT', `${PREFERENCES_ROSTER}/last`, setLastFiltersRequestSchema.parse({ filters }), { keepalive: true }));
}

export async function createSavedFilter(input: { readonly name: string; readonly filters: RosterFilterPreference }): Promise<{ readonly saved: SavedRosterFilter; readonly view: PreferencesView }> {
  return createSavedFilterResponseSchema.parse(await preferencesRequest('POST', `${PREFERENCES_ROSTER}/saved`, createSavedFilterRequestSchema.parse(input)));
}

export async function renameSavedFilter(id: string, name: string): Promise<PreferencesView> {
  return preferencesViewSchema.parse(await preferencesRequest('PATCH', `${PREFERENCES_ROSTER}/saved/${encodeURIComponent(id)}`, renameSavedFilterRequestSchema.parse({ name })));
}

export async function deleteSavedFilter(id: string): Promise<PreferencesView> {
  return preferencesViewSchema.parse(await preferencesRequest('DELETE', `${PREFERENCES_ROSTER}/saved/${encodeURIComponent(id)}`));
}

/** Removes every saved filter this build cannot show (written by another version). */
export async function deleteUnreadableSavedFilters(): Promise<PreferencesView> {
  return preferencesViewSchema.parse(await preferencesRequest('DELETE', `${PREFERENCES_ROSTER}/saved/unreadable`));
}


/* ---- The place picker (ADR-018, #473) ------------------------------------------------------------ */

/**
 * The browser talks to OUR server for places and never to Google. A search sends the letters, the
 * kind and a made-up session token; the names read sends place ids. Neither sends anything about a
 * person. A refusal has the same shape as a preferences refusal (`code` is the API's word, such as
 * `places-unavailable`, `rate-limited` or `not-virginia`).
 */
export type PlacesError = PreferencesError;

export async function searchPlaces(input: PlaceAutocompleteRequest): Promise<PlaceAutocompleteResponse> {
  return placeAutocompleteResponseSchema.parse(await preferencesRequest('POST', '/api/places/autocomplete', placeAutocompleteRequestSchema.parse(input)));
}

export async function resolvePlace(input: PlaceResolveRequest): Promise<PlaceResolveResponse> {
  return placeResolveResponseSchema.parse(await preferencesRequest('POST', '/api/places/resolve', placeResolveRequestSchema.parse(input)));
}

/** The words for some place ids. At most `PLACE_NAMES_MAX` at a time; the caller splits a longer list. */
export async function fetchPlaceNames(ids: readonly string[]): Promise<PlaceNamesResponse> {
  return placeNamesResponseSchema.parse(await preferencesRequest('GET', `/api/places/names?ids=${ids.map(encodeURIComponent).join(',')}`));
}

/**
 * The coverage map's picture addresses (#474). Our server reads the places' positions from Google and signs the
 * addresses; the browser only names places. It is called when HR opens the map and not before (each picture load
 * is billed). A refusal carries the API's word (`map-unavailable`, `rate-limited`).
 */
export async function fetchCoverageMap(ids: readonly string[]): Promise<CoverageMapResponse> {
  return coverageMapResponseSchema.parse(await preferencesRequest('GET', `/api/places/coverage-map?ids=${ids.map(encodeURIComponent).join(',')}`));
}
